import React, { useEffect, useState } from 'react'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import {
  CheckCircleIcon,
  ClockIcon,
  AlertCircleIcon,
  Loader2Icon,
  MapPinIcon,
  CalendarIcon,
  DollarSignIcon,
  UndoIcon,
  ClipboardCheckIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { fetchJobTimeEntries, type JobTimeEntrySummary } from '../api/jobRelatedRecords'
import { getStatusColor, formatStatusLabel } from '../lib/jobStatusWorkflow'
import { JobTaskChecklist } from '../components/job/JobTaskChecklist'
import { ApartmentJobWork } from '../components/job/ApartmentJobWork'
import { JobGallery } from '../components/job/JobGallery'
import { usePermissions } from '../hooks/usePermissions'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { ViewTabBar, ViewSection, ViewEmptyState } from '../components/ViewPageLayout'

interface JobDetail {
  id: string
  title: string
  job_number?: string
  status: string
  scheduled_date?: string
  actual_start_time?: string
  actual_end_time?: string
  actual_amount?: number
  quoted_amount?: number
  priority?: string
  notes?: string
  description?: string
  contract_id?: string | null
  service_type?: string | null
  portal_published_at?: string | null
  customer?: {
    id: string
    company_name: string
    contact_first_name?: string
    contact_last_name?: string
    email?: string
    phone?: string
  } | null
  property?: {
    id: string
    name?: string
    address?: string
    city?: string
    state?: string
    zip_code?: string
  } | null
}

interface InspectorOption {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
}

export function ReviewDetails() {
  const { can, canSeePricing, loading: permLoading } = usePermissions()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [job, setJob] = useState<JobDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const [timeEntries, setTimeEntries] = useState<JobTimeEntrySummary[]>([])
  const [activeTab, setActiveTab] = useState<'checklist' | 'photos' | 'time'>('checklist')

  const [showQcForm, setShowQcForm] = useState(false)
  const [inspectors, setInspectors] = useState<InspectorOption[]>([])
  const [qcDate, setQcDate] = useState('')
  const [qcInspectorId, setQcInspectorId] = useState('')

  useEffect(() => {
    if (id) {
      fetchJob(id)
      loadTimeEntries(id)
    }
  }, [id])

  const fetchJob = async (jobId: string) => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select('*, customer:customers(*), property:properties(*)')
        .eq('id', jobId)
        .single()

      if (fetchError) throw fetchError
      setJob(data)
    } catch (err: any) {
      console.error('Error fetching job:', err)
      setError(err.message || 'Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const loadTimeEntries = async (jobId: string) => {
    try {
      const entries = await fetchJobTimeEntries(jobId)
      setTimeEntries(entries)
    } catch (err) {
      console.error('Error loading time entries:', err)
      setTimeEntries([])
    }
  }

  const loadInspectors = async () => {
    const { data } = await supabase
      .from('employees')
      .select('id, first_name, last_name, employee_number')
      .eq('is_active', true)
      .order('first_name')
    setInspectors((data || []) as InspectorOption[])
  }

  const handleStatusUpdate = async (newStatus: string) => {
    if (!job) return
    if (!can('review.approve')) {
      alert('You do not have permission to perform this review action.')
      return
    }
    try {
      setUpdating(true)
      const updateData: { status: string; portal_published_at?: string | null } = { status: newStatus }
      if (newStatus === 'approved') {
        // Approved jobs are only visible in the client portal once explicitly published.
        const publish = window.confirm(
          "Publish this job's report and photos to the customer portal?"
        )
        updateData.portal_published_at = publish ? new Date().toISOString() : null
      }
      const { error: updateError } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', job.id)

      if (updateError) throw updateError
      setJob({ ...job, ...updateData })
    } catch (err: any) {
      console.error('Error updating job status:', err)
      alert('Failed to update status: ' + err.message)
    } finally {
      setUpdating(false)
    }
  }

  const openScheduleQc = () => {
    setShowQcForm(true)
    setQcDate('')
    setQcInspectorId('')
    if (inspectors.length === 0) loadInspectors()
  }

  const handleScheduleQc = async () => {
    if (!job || !qcDate) return
    try {
      setUpdating(true)
      const { error: insertError } = await supabase.from('qc_inspections').insert({
        job_id: job.id,
        contract_id: job.contract_id || null,
        inspector_id: qcInspectorId || null,
        scheduled_date: qcDate,
        status: 'scheduled',
      })
      if (insertError) throw insertError

      await handleStatusUpdate('qc_scheduled')
      setShowQcForm(false)
    } catch (err: any) {
      console.error('Error scheduling QC visit:', err)
      alert('Failed to schedule QC visit: ' + (err.message || 'Unknown error'))
    } finally {
      setUpdating(false)
    }
  }

  const calculateHours = (): string | null => {
    if (job?.actual_start_time && job?.actual_end_time) {
      const start = new Date(job.actual_start_time)
      const end = new Date(job.actual_end_time)
      const diffMs = end.getTime() - start.getTime()
      if (diffMs > 0) return (diffMs / (1000 * 60 * 60)).toFixed(1)
    }
    return null
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-red-800 mb-2">Job Not Found</h2>
            <p className="text-red-600 mb-4">{error || 'The requested job could not be found.'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  const hours = calculateHours()
  const totalTimeHours = timeEntries.reduce((sum, e) => sum + Number(e.total_hours || 0), 0)
  const canApproveReview = can('review.approve')
  const canApprove = canApproveReview && job.status !== 'approved'
  const canSendBack = canApproveReview && job.status !== 'in_progress' && job.status !== 'scheduled' && job.status !== 'draft'
  const canScheduleQc = canApproveReview && ['pending_review', 'under_review'].includes(job.status)

  if (!permLoading && !can('review.view')) {
    return <Navigate to="/access-denied" replace />
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={job.title}
        subtitle={job.job_number ? `Job #${job.job_number}` : `Job ID: ${job.id.slice(0, 8)}...`}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(job.status)}`}>
            {formatStatusLabel(job.status)}
          </span>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <ViewSection title="Job Details" icon={<ClipboardCheckIcon className="w-6 h-6" />} tone="blue">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Scheduled Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {job.scheduled_date
                        ? new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()
                        : '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-sm font-medium text-gray-900">
                      {job.actual_end_time ? new Date(job.actual_end_time).toLocaleString() : '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="text-sm font-medium text-gray-900">
                      {hours ? `${hours} hours` : totalTimeHours > 0 ? `${totalTimeHours.toFixed(1)} hours` : '—'}
                    </p>
                  </div>
                </div>
                {canSeePricing && (
                <div className="flex items-start gap-3">
                  <DollarSignIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-sm font-medium text-gray-900">
                      $
                      {Number(job.actual_amount || job.quoted_amount || 0).toLocaleString(
                        undefined,
                        { minimumFractionDigits: 2 }
                      )}
                    </p>
                  </div>
                </div>
                )}
              </div>
              {job.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-900">{job.description}</p>
                </div>
              )}
              {job.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Notes</p>
                  <p className="text-sm text-gray-900">{job.notes}</p>
                </div>
              )}
            </ViewSection>

            <div className={`${mobile.surface} flex flex-col min-h-0`}>
              <ViewTabBar
                tabs={[
                  { id: 'checklist', label: 'Checklist' },
                  { id: 'photos', label: 'Photos' },
                  { id: 'time', label: 'Time', full: 'Time Entries' },
                ]}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
              <div className="p-3 sm:p-6">
                {activeTab === 'checklist' && (
                  <div className="space-y-4">
                    <ApartmentJobWork jobId={job.id} serviceType={job.service_type} />
                    <JobTaskChecklist
                      jobId={job.id}
                      contractId={job.contract_id}
                      serviceType={job.service_type}
                      generalOnly
                    />
                  </div>
                )}
                {activeTab === 'photos' && <JobGallery jobId={job.id} />}
                {activeTab === 'time' && (
                  <div className="space-y-2">
                    {timeEntries.length === 0 ? (
                      <ViewEmptyState tone="sky" message="No time entries logged for this job." />
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 uppercase">
                            <th className="py-2">Employee</th>
                            <th className="py-2">Clock In</th>
                            <th className="py-2">Clock Out</th>
                            <th className="py-2">Hours</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {timeEntries.map((entry) => (
                            <tr key={entry.id}>
                              <td className="py-2 text-gray-900">
                                {entry.employee
                                  ? `${entry.employee.first_name || ''} ${entry.employee.last_name || ''}`.trim()
                                  : 'Unassigned'}
                              </td>
                              <td className="py-2 text-gray-600">
                                {entry.clock_in_time ? new Date(entry.clock_in_time).toLocaleString() : '—'}
                              </td>
                              <td className="py-2 text-gray-600">
                                {entry.clock_out_time ? new Date(entry.clock_out_time).toLocaleString() : '—'}
                              </td>
                              <td className="py-2 font-medium text-gray-900">
                                {entry.total_hours != null ? `${Number(entry.total_hours).toFixed(2)}h` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <p className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                      Total: {totalTimeHours.toFixed(2)} hours
                    </p>
                  </div>
                )}
              </div>
            </div>

            {job.customer && (
              <ViewSection title="Customer" icon={<CheckCircleIcon className="w-6 h-6" />} tone="teal">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">{job.customer.company_name}</p>
                  {(job.customer.contact_first_name || job.customer.contact_last_name) && (
                    <p className="text-sm text-gray-600">
                      Contact: {job.customer.contact_first_name} {job.customer.contact_last_name}
                    </p>
                  )}
                  {job.customer.email && (
                    <p className="text-sm text-gray-600">{job.customer.email}</p>
                  )}
                  {job.customer.phone && (
                    <p className="text-sm text-gray-600">{job.customer.phone}</p>
                  )}
                </div>
              </ViewSection>
            )}

            {job.property && (
              <ViewSection title="Property" icon={<MapPinIcon className="w-6 h-6" />} tone="sky">
                <div className="flex items-start gap-3">
                  <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    {job.property.name && (
                      <p className="text-sm font-medium text-gray-900">{job.property.name}</p>
                    )}
                    <p className="text-sm text-gray-600">
                      {[
                        job.property.address,
                        job.property.city,
                        job.property.state,
                        job.property.zip_code,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </div>
                </div>
              </ViewSection>
            )}
          </div>

          <div className="space-y-6">
            <ViewSection title="Review Actions" icon={<CheckCircleIcon className="w-6 h-6" />} tone="green">
              <div className="space-y-3">
                {canApprove && (
                  <button
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={updating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    {updating ? 'Updating...' : 'Approve Job'}
                  </button>
                )}
                {canScheduleQc && !showQcForm && (
                  <button
                    onClick={openScheduleQc}
                    disabled={updating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    <ClipboardCheckIcon className="w-4 h-4" />
                    Schedule QC
                  </button>
                )}
                {showQcForm && (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <label className="block text-xs font-medium text-gray-700">QC Date</label>
                    <input
                      type="date"
                      value={qcDate}
                      onChange={(e) => setQcDate(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <label className="block text-xs font-medium text-gray-700">Inspector</label>
                    <select
                      value={qcInspectorId}
                      onChange={(e) => setQcInspectorId(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Unassigned</option>
                      {inspectors.map((i) => (
                        <option key={i.id} value={i.id}>
                          {formatEmployeeLabel(i)}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={handleScheduleQc}
                        disabled={updating || !qcDate}
                        className="flex-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setShowQcForm(false)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {canSendBack && (
                  <button
                    onClick={() => handleStatusUpdate('in_progress')}
                    disabled={updating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                  >
                    <UndoIcon className="w-4 h-4" />
                    {updating ? 'Updating...' : 'Send Back for Rework'}
                  </button>
                )}
              </div>
            </ViewSection>

            <ViewSection title="Summary" icon={<ClockIcon className="w-6 h-6" />} tone="indigo">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium">{formatStatusLabel(job.status)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Priority:</span>
                  <span className="font-medium capitalize">{job.priority || '—'}</span>
                </div>
                {job.status === 'approved' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Client Portal:</span>
                    <span className={`font-medium ${job.portal_published_at ? 'text-green-700' : 'text-gray-500'}`}>
                      {job.portal_published_at ? 'Published' : 'Not published'}
                    </span>
                  </div>
                )}
                {(hours || totalTimeHours > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">{hours ? `${hours}h` : `${totalTimeHours.toFixed(1)}h`}</span>
                  </div>
                )}
                {canSeePricing && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium">
                    $
                    {Number(job.actual_amount || job.quoted_amount || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                )}
              </div>
            </ViewSection>
          </div>
        </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={() => navigate(-1)} className={mobile.secondaryBtn}>
          Back
        </button>
      </div>
    </MobilePage>
  )
}
